import React, { useMemo } from 'react';
import classNames from 'classnames';
import {
  getTPASettignsLiveSiteEditingDeprecated,
  isLayoutNameList,
  type LayoutName,
} from '@wix/communities-blog-client-common';
import { useFeedMetadataSettings } from '../../hooks/use-feed-metadata-settings';
import useIsFeedDesignEnabled from '../../hooks/use-is-feed-design-enabled';
import {
  getLayoutDescriptionLineCount,
  getLayoutTitleLineCount,
} from '../../selectors/app-settings-selectors';
import { type LayoutItemConfig } from '../../services/layout-config';
import styles from '../../styles/post-list-item.scss';
import { type NormalizedPost } from '../../types';
import PostLink from '../link/post-link';
import { useSelector } from '../runtime-context';
import { PostListItemProGalleryDescription } from './post-list-item-pro-gallery-description';
import { PostListItemProGalleryTitleDescriptionTitle } from './post-list-item-pro-gallery-title-description-title';

type Props = {
  type: LayoutName | undefined;
  post: NormalizedPost;
  itemConfig: Partial<LayoutItemConfig>;
  showMoreButton: boolean;
  withoutFooter: boolean;
};

export const PostListItemProGalleryTitleDescription = ({
  post,
  itemConfig,
  type,
  showMoreButton,
  withoutFooter,
}: Props) => {
  const titleLineCount = useSelector((state) => getLayoutTitleLineCount({ state }));
  const descriptionLineCount = useSelector((state) => getLayoutDescriptionLineCount({ state }));
  const isLiveSiteEditingDeprecated = useSelector(getTPASettignsLiveSiteEditingDeprecated);
  const { getPostClassName, section } = useIsFeedDesignEnabled();
  const isHoverDisabled =
    isLiveSiteEditingDeprecated && section === 'my-posts' && post?.status === 'unpublished';
  const lineCount =
    itemConfig.lineCount && 'description' in itemConfig.lineCount && itemConfig.lineCount
      ? itemConfig.lineCount
      : undefined;
  const layoutList = isLayoutNameList(type);
  const feedMetadataSettings = useFeedMetadataSettings({ post });
  const withDescription = feedMetadataSettings.showPostDescription && !layoutList;
  const layoutTypeClassName = type ? styles[type] : undefined;

  const titleContainer = useMemo(
    () =>
      classNames(
        styles.textWrapper,
        {
          [getPostClassName('link-hashtag-hover-color').join(' ')]: !isHoverDisabled,
          [styles.fadeout]: descriptionLineCount || titleLineCount,
        },
        layoutTypeClassName,
        feedMetadataSettings.showCategoryLabel && styles.withCategoryLabel,
      ),
    [
      isHoverDisabled,
      descriptionLineCount,
      titleLineCount,
      layoutTypeClassName,
      feedMetadataSettings.showCategoryLabel,
      getPostClassName,
    ],
  );

  const titleLinkClassName = useMemo(
    () =>
      classNames(
        styles.titleWrapper,
        styles.proGallery,
        layoutTypeClassName,
        showMoreButton ? styles.withShowMoreButton : styles.withoutShowMoreButton,
      ),
    [layoutTypeClassName, showMoreButton],
  );

  return (
    <div className={titleContainer}>
      {isHoverDisabled ? (
        <PostListItemProGalleryTitleDescriptionTitle
          type={type}
          withoutFooter={withoutFooter}
          post={post}
          withDescription={withDescription}
          showMoreButton={showMoreButton}
          lineCount={lineCount}
          isHoverDisabled={isHoverDisabled}
        />
      ) : (
        <PostLink
          postLink={isHoverDisabled ? undefined : post?.link}
          postPath={post?.slug!}
          className={titleLinkClassName}
        >
          <PostListItemProGalleryTitleDescriptionTitle
            type={type}
            withoutFooter={withoutFooter}
            lineCount={lineCount}
            post={post}
            withDescription={withDescription}
            showMoreButton={showMoreButton}
            isHoverDisabled={isHoverDisabled}
          />
        </PostLink>
      )}
      <PostListItemProGalleryDescription
        post={post}
        lineCount={lineCount}
        type={type}
        showMoreButton={showMoreButton}
        withoutFooter={withoutFooter}
        descriptionLineCount={descriptionLineCount}
        isHoverDisabled={isHoverDisabled}
        withDescription={withDescription}
      />
    </div>
  );
};
